import { ref, reactive } from 'vue'

type ConfirmType = 'info' | 'warning' | 'error' | 'success' | 'question'

interface ConfirmData {
  title?: string
  message: string
  type?: ConfirmType
  cancelText?: string
  confirmText?: string
  closeOnBackdrop?: boolean
  closeOnEscape?: boolean
}

interface ConfirmOptions extends ConfirmData {
  onConfirm?: () => void
  onCancel?: () => void
}

const isVisible = ref(false)
const confirmData = reactive<ConfirmData>({
  message: '',
  type: 'question',
  cancelText: 'Cancelar',
  confirmText: 'Confirmar',
  closeOnBackdrop: true,
  closeOnEscape: true
})

let currentResolve: ((value: boolean) => void) | null = null
let onConfirmCallback: (() => void) | null = null
let onCancelCallback: (() => void) | null = null

export function useGlobalConfirm() {
  const openConfirm = (options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      // Store callbacks
      currentResolve = resolve
      onConfirmCallback = options.onConfirm || null
      onCancelCallback = options.onCancel || null

      // Set confirm data
      Object.assign(confirmData, {
        title: options.title,
        message: options.message,
        type: options.type || 'question',
        cancelText: options.cancelText || 'Cancelar',
        confirmText: options.confirmText || 'Confirmar',
        closeOnBackdrop: options.closeOnBackdrop ?? true,
        closeOnEscape: options.closeOnEscape ?? true
      })

      // Show confirm dialog
      isVisible.value = true
    })
  }

  const closeConfirm = (result: boolean) => {
    isVisible.value = false
    
    if (currentResolve) {
      currentResolve(result)
    }

    if (result && onConfirmCallback) {
      onConfirmCallback()
    } else if (!result && onCancelCallback) {
      onCancelCallback()
    }

    // Reset callbacks
    currentResolve = null
    onConfirmCallback = null
    onCancelCallback = null

    // Reset confirm data
    Object.assign(confirmData, {
      title: undefined,
      message: '',
      type: 'question',
      cancelText: 'Cancelar',
      confirmText: 'Confirmar',
      closeOnBackdrop: true,
      closeOnEscape: true
    })
  }

  // Convenience methods
  const confirmDelete = (itemName?: string, options?: Partial<ConfirmOptions>) => {
    const message = itemName 
      ? `Tem certeza que deseja excluir "${itemName}"? Esta ação não pode ser desfeita.`
      : 'Tem certeza que deseja excluir este item? Esta ação não pode ser desfeita.'
    
    return openConfirm({
      title: 'Confirmar Exclusão',
      message,
      type: 'error',
      confirmText: 'Excluir',
      ...options
    })
  }

  const confirmAction = (action: string, message?: string, options?: Partial<ConfirmOptions>) => {
    return openConfirm({
      title: `Confirmar ${action}`,
      message: message || `Tem certeza que deseja ${action.toLowerCase()}?`,
      type: 'warning',
      confirmText: action,
      ...options
    })
  }

  const confirmInfo = (title: string, message: string, options?: Partial<ConfirmOptions>) => {
    return openConfirm({
      title,
      message,
      type: 'info',
      cancelText: 'Fechar',
      confirmText: 'OK',
      ...options
    })
  }

  const confirmSuccess = (title: string, message: string, options?: Partial<ConfirmOptions>) => {
    return openConfirm({
      title,
      message,
      type: 'success',
      cancelText: 'Fechar',
      confirmText: 'OK',
      ...options
    })
  }

  const confirmError = (title: string, message: string, options?: Partial<ConfirmOptions>) => {
    return openConfirm({
      title,
      message,
      type: 'error',
      cancelText: 'Fechar',
      confirmText: 'OK',
      ...options
    })
  }

  const confirmWarning = (title: string, message: string, options?: Partial<ConfirmOptions>) => {
    return openConfirm({
      title,
      message,
      type: 'warning',
      confirmText: 'Continuar',
      ...options
    })
  }

  return {
    isVisible,
    confirmData,
    openConfirm,
    closeConfirm,
    confirmDelete,
    confirmAction,
    confirmInfo,
    confirmSuccess,
    confirmError,
    confirmWarning
  }
}

// Global instance for use in plugins or outside of components
export const globalConfirm = useGlobalConfirm()